import { generators } from '@playcastdotio/Messaging';
import {
  hostMatchmakingState,
  authState,
  hostState,
  invitationStateV2,
  matchmakerState,
  steamCredentialsState,
} from '../../stores/shared';
import { checkPlayerIds } from '../peers/host';
import { ipcSend } from '../../messaging/protocols/ipc';
import { getIpcFunctions } from '../ipcInterface';
import { checkSoftKickExpiration } from '../managers/softKick';
import { sendMatchmakerHostConfig } from '../managers/matchmaker';
import { getStateStore } from '../../stores';
import { USE_LEGACY_PLAYJECTOR_EVENTS, USE_MESSAGE_ROUTER } from '../../../config/featureFlags';

const state = getStateStore('ipc');

export const initializeInvitationEvents = () => {
  let cachedInvitationState = invitationStateV2.currentState;
  invitationStateV2.addListener((invState) => {
    console.log('Invitation state changed:', invState);
    if (hostState.currentState.edition === 'pavilion') {
      // We want to track changes to the players in the invitation. If the players change, we want to ensure we kick them.
      if (invState.type === 'cafe') {
        console.log('[INVITATION EVENTS] Soft kick expiration time:', invState.manifest?.softKickExpirationTime);
        if (invState.manifest?.softKickExpirationTime) {
          // Check if soft kick has expired and handle session invalidation if needed
          checkSoftKickExpiration(invState);
        }

        // Check if invitation is invalidated and clean up session
        // This handles forced invalidations (soft kick, admin actions, API cleanup)
        // Kicks players FIRST to trigger Ad Mode restart, then stops stream
        if (invState.invalid && cachedInvitationState && !cachedInvitationState.invalid) {
          console.log('[Invitation] Invitation invalidated, cleaning up session');
          try {
            const ipcFunctions = getIpcFunctions();

            // CRITICAL: Kick players FIRST before stopping stream
            // This ensures playerCount drops to 0, triggering Ad Mode restart
            const playerIds = Object.keys(state.playerState.currentState.players);
            if (playerIds.length > 0) {
              console.log('[Invitation] Kicking remaining players due to invalidation:', playerIds);
              ipcFunctions?.kickAll();
            }

            // Stop the stream if still streaming
            // stopStream() will also kick players, but they're already gone (harmless)
            if (hostState.currentState.status === 'streaming') {
              console.log('[Invitation] Stopping stream due to invalidation');
              ipcFunctions?.stopStream('host:invitation_invalidated');
            }

            // Reset matchmaker state
            matchmakerState.reset();
          } catch (error) {
            console.error('[Invitation] Failed to clean up session on invalidation:', error);
          }
        }

        // Handle soft kick invalidation cleanup
        if (invState.invalid && cachedInvitationState && !cachedInvitationState.invalid) {
          if (invState.manifest?.softKickExpirationTime) {
            console.log(`[SoftKick] Received invalidation confirmation for invitation ${invState.id}, performing cleanup`);

            // Clear invitation state and localStorage
            invitationStateV2.reset();
            localStorage.removeItem('currentInvitation');

            // Remove host from matchmaking
            hostMatchmakingState.updateState({
              ...hostMatchmakingState.currentState,
              matchmakeable: false,
            });

            // TODO-STRANGLER-FIG: Legacy HOST_CONFIG send - remove when PavilionCoordinator fully owns this
            if (!USE_MESSAGE_ROUTER) {
              // Send the updated config to the API
              sendMatchmakerHostConfig().catch((err) => {
                console.error('[SoftKick] Failed to send host config after cleanup:', err);
              });
            }

            console.log(`[SoftKick] Successfully completed cleanup for soft kick of invitation ${invState.id}`);
          }
        }

        if (cachedInvitationState?.type === 'cafe') {
          const oldPlayerIds = cachedInvitationState?.manifest?.players.map((player) => player.playerId || '') || [];
          const newPlayerIds = invState.manifest?.players.map((player) => player.playerId || '') || [];
          const removedPlayerIds = oldPlayerIds.filter((id) => !newPlayerIds.includes(id));
          checkPlayerIds(newPlayerIds, removedPlayerIds);
        }

        // Send the useSteamGuestLogin to playjector when it changes
        const currentSteamGuestLogin = invState.manifest?.useSteamGuestLogin ?? false;
        const cachedSteamGuestLogin =
          cachedInvitationState?.type === 'cafe' ? (cachedInvitationState.manifest?.useSteamGuestLogin ?? false) : false;

        // TODO-STRANGLER-FIG: Legacy IPC send - remove when PavilionCoordinator fully owns this
        // Only send hostModeChanged if:
        // 1. This is the first invitation (no cached state)
        // 2. The steam guest login setting has changed
        // 3. The invitation ID has changed (new invitation)
        const shouldSendUpdate =
          !cachedInvitationState || currentSteamGuestLogin !== cachedSteamGuestLogin || cachedInvitationState.id !== invState.id;

        if (USE_LEGACY_PLAYJECTOR_EVENTS && shouldSendUpdate) {
          console.log('[Invitation] Updating playjector with steam guest login:', currentSteamGuestLogin);

          ipcSend(
            generators.host.hostModeChanged(
              'pavilion',
              hostMatchmakingState.currentState.matchmakeable,
              currentSteamGuestLogin,
              hostMatchmakingState.currentState.guestAccount,
              steamCredentialsState.currentState,
              authState.currentState.hostAuthId,
            ),
          );
        }

        const lobbyOwnerChanged = cachedInvitationState.owner.profileId !== invState.owner.profileId;
        if (lobbyOwnerChanged) {
          const [ownerId, owner] =
            Object.entries(state.playerState.currentState.players).find(
              ([_, player]) => player.playerAuthId === invState.owner.profileId || player.persistentId === invState.owner.profileId,
            ) || [];

          if (ownerId && owner) {
            state.playerState.updateState({
              ...state.playerState.currentState,
              players: {
                ...state.playerState.currentState.players,
                [ownerId]: {
                  ...owner,
                  access: {
                    gamepad: true,
                    keyboard: true,
                    mouse: true,
                    touch: false,
                    chat: false,
                  },
                },
              },
            });
          }
        }
      }
    }
    cachedInvitationState = invState;
  });
};
